import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

const SimpleNumberInput = React.forwardRef((props, ref) => {
  const { value, onChange, min, max = Infinity, label, disabled, onEnterPress, onPrevPress, showLabels = true } = props;

  useEffect(() => {
    if (value > max) {
      onChange(max);
    }
  }, [max, value, onChange]);

  const inputValue = value === undefined ? min : value;

  const increment = () => {
    if (!disabled) {
      onChange(Math.min(inputValue + 1, max));
    }
  };

  const decrement = () => {
    if (!disabled) {
      onChange(Math.max(inputValue - 1, min));
    }
  };

  const handleChange = (e) => {
    if (!disabled) {
      const newValue = e.target.value === '' ? '' : parseInt(e.target.value, 10);
      if (newValue === '' || (!isNaN(newValue) && newValue >= min && newValue <= max)) {
        onChange(newValue);
      }
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'ArrowUp' || (e.key === 'Enter' && e.shiftKey)) && onPrevPress) {
      e.preventDefault();
      onPrevPress();
    }
    else if ((e.key === 'ArrowDown' || (e.key === 'Enter')) && onEnterPress) {
      e.preventDefault();
      onEnterPress();
    }
  };  

  return (
    <div>
      {showLabels && (<label htmlFor="quantity-input" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        {label || 'Choose quantity'} ({min} to {max === Infinity ? 'No limit' : max}):
      </label>) }
      <div className="relative flex items-center max-w-[8rem]">
        <button
          type="button"
          onClick={decrement}
          className={`bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 hover:bg-gray-200 border border-gray-300 rounded-l-lg p-3 h-11 focus:ring-gray-100 dark:focus:ring-gray-700 focus:ring-2 focus:outline-none ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={disabled}
        >
          &#8722; {/* Minus sign */}
        </button>
        <input
          type="text"
          id="quantity-input"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          ref={ref} // Use the forwarded ref here
          className={`bg-gray-50 border-x-0 border-gray-300 h-11 text-center text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 ${
            disabled ? 'bg-gray-200' : ''
          }`}
          required
          disabled={disabled}
        />
        <button
          type="button"
          onClick={increment}
          className={`bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 hover:bg-gray-200 border border-gray-300 rounded-r-lg p-3 h-11 focus:ring-gray-100 dark:focus:ring-gray-700 focus:ring-2 focus:outline-none ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={disabled}
        >
          &#43; {/* Plus sign */}
        </button>
      </div>
    </div>
  );
});

SimpleNumberInput.displayName = 'SimpleNumberInput';

SimpleNumberInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onEnterPress: PropTypes.func,
  onPrevPress: PropTypes.func,
  showLabels: PropTypes.bool
};

SimpleNumberInput.defaultProps = {
  showLabels: true,
  disabled: false,
  max: Infinity,
  onPrevPress: undefined,
};

export default SimpleNumberInput;
